import { SearchQuery, SearchResult, defineEndpoint } from '@itc/shared';
import { z } from 'zod';
import {
  BiocodeResult,
  EditInput,
  JoinAgreementInput,
  JoinPosted,
  JoinReply,
  JoinStartInput,
  ListQuery,
  MemberDetail,
  MemberId,
  MemberImportInput,
  MemberPage,
  MembersImportReport,
  OrgImportInput,
  PardonInput,
  PurgeInput,
  Purged,
  RescrapeResult,
  ReviewPage,
  ReviewQuery,
  RsiCredentialsInput,
  RsiStatus,
  RsiTest,
  SyncOverview,
} from './schemas.ts';
import {
  ArchivePage,
  ArchiveImportInput,
  ArchiveQuery,
  CsvFile,
  History,
  HistoryImportContext,
  HistoryImportReport,
  ReportView,
  RoleCountsQuery,
  RoleCountsView,
  SampleImportInput,
  SnapshotImportInput,
  ViewReportParam,
} from './report-schemas.ts';

// Members' contracts (design §7.3, plan 5B): the roster, the RSI account's cookies, the sync and /join.

/** A page of profiles by handle, filtered and searched on the server (design §6.12). */
export const listMembers = defineEndpoint({ method: 'GET', path: '/members', permission: 'members.view', query: ListQuery, response: MemberPage });

/** One profile, at the caller's tier. */
export const getMember = defineEndpoint({ method: 'GET', path: '/members/:id', permission: 'members.view', params: z.object({ id: MemberId }), response: MemberDetail });

/** The profiles held by the threat system: a ban, a flag or evasion. Reasons, orgs and history, for those who may read them. */
export const getReviewQueue = defineEndpoint({ method: 'GET', path: '/members/review', permission: 'members.manage', query: ReviewQuery, response: ReviewPage });

/** What global search finds: nothing without members.view. */
export const searchMembers = defineEndpoint({ method: 'GET', path: '/members/search', permission: 'authenticated', query: SearchQuery, response: SearchResult });

/** Whether RSI's cookies are set, the account's handle and the status. Never the cookies. */
export const getRsiStatus = defineEndpoint({ method: 'GET', path: '/members/admin/rsi', permission: 'members.admin', response: RsiStatus });

/** Seals new cookies and tests them at once. The old ones are replaced even if the test fails, so the page can say why. */
export const setRsiCredentials = defineEndpoint({
  method: 'PUT',
  path: '/members/admin/rsi/credentials',
  permission: 'members.admin',
  recentLogin: true,
  rateLimit: { max: 10 },
  body: RsiCredentialsInput,
  response: RsiTest,
});

/** Tests the stored cookies. A pass lets logged-in work run again. */
export const testRsiCredentials = defineEndpoint({ method: 'POST', path: '/members/admin/rsi/test', permission: 'members.admin', rateLimit: { max: 10 }, response: RsiTest });

// --- /join (R5) -------------------------------------------------------------------------
// All `discord`: any member of the guild through a Discord interaction, never a browser.
// Each answers with the step the person is at and what to show. Only the Discord handlers
// call them. The ones that can reach RSI have a smaller budget than the default.

/** Where this person is: a run under way, or the start. Changes nothing. */
export const getJoin = defineEndpoint({ method: 'GET', path: '/members/join', permission: 'discord', response: JoinReply });

/** Begins with a handle: reads the profile, gives a code. Writes no profile. */
export const startJoin = defineEndpoint({ method: 'POST', path: '/members/join/start', permission: 'discord', rateLimit: { max: 10 }, body: JoinStartInput, response: JoinReply });

/** Checks the code is in the bio. On success the link is set, and the assessment runs. */
export const verifyJoin = defineEndpoint({ method: 'POST', path: '/members/join/verify', permission: 'discord', rateLimit: { max: 10 }, response: JoinReply });

/** A new code in place of the one given. */
export const regenerateJoinCode = defineEndpoint({ method: 'POST', path: '/members/join/code', permission: 'discord', rateLimit: { max: 10 }, response: JoinReply });

/** Reads the profile again after hidden orgs were shown. */
export const recheckJoin = defineEndpoint({ method: 'POST', path: '/members/join/recheck', permission: 'discord', rateLimit: { max: 10 }, response: JoinReply });

/** The anti-piracy agreement: agreed, or not. */
export const answerJoinAgreement = defineEndpoint({ method: 'POST', path: '/members/join/agreement', permission: 'discord', rateLimit: { max: 10 }, body: JoinAgreementInput, response: JoinReply });

/** Posts the persistent Join button to the channel in `joinChannelId`. 400 until that is set. */
export const postJoinButton = defineEndpoint({ method: 'POST', path: '/members/admin/join-button', permission: 'members.admin', rateLimit: { max: 10 }, response: JoinPosted });

/** How the roster and public passes stand: last pass, counts, last error. */
export const getSyncStatus = defineEndpoint({ method: 'GET', path: '/members/admin/sync', permission: 'members.manage', response: SyncOverview });

/** Reads the whole roster now. 409 while one is running, or when the cookies aren't working. Answers at once; the pass goes on. */
export const runRosterSync = defineEndpoint({ method: 'POST', path: '/members/admin/sync/roster', permission: 'members.manage', rateLimit: { max: 10 }, response: SyncOverview });

/** Works through the public pages now, starting a pass if none is under way. 409 while a run is going. Answers at once. */
export const runPublicSync = defineEndpoint({ method: 'POST', path: '/members/admin/sync/public', permission: 'members.manage', rateLimit: { max: 10 }, response: SyncOverview });

// --- The tools on one profile (Chunk 6). Each is audited with who did it. ----------------

const ById = z.object({ id: MemberId });

/** Reads the handle's RSI pages now and applies R2. A failed read changes nothing. Ten a minute, as each is several requests to RSI. */
export const rescrapeMember = defineEndpoint({ method: 'POST', path: '/members/:id/rescrape', permission: 'members.manage', params: ById, rateLimit: { max: 10 }, response: RescrapeResult, idempotencyKey: 'required' });

/** Gives the profile a new bio code, for the person to put in their RSI bio. The old one stops working at once. */
export const generateBiocode = defineEndpoint({ method: 'POST', path: '/members/:id/biocode', permission: 'members.manage', params: ById, rateLimit: { max: 10 }, response: BiocodeResult, idempotencyKey: 'required' });

/** Clears a ban, a flag, evasion and their reasons, with a reason of its own. The org history stays. */
export const pardonMember = defineEndpoint({ method: 'POST', path: '/members/:id/pardon', permission: 'members.manage', params: ById, body: PardonInput, response: MemberDetail, idempotencyKey: 'required' });

/** One named edit, with a reason: the link, the flags, the agreement or the ban reason. */
export const editMember = defineEndpoint({ method: 'POST', path: '/members/:id/edit', permission: 'members.admin', recentLogin: true, params: ById, body: EditInput, response: MemberDetail, idempotencyKey: 'required' });

/** Deletes the profile for good. The body must hold the handle (or the id, if there is none), checked here as well as on the page. */
export const purgeMember = defineEndpoint({ method: 'POST', path: '/members/:id/purge', permission: 'members.admin', recentLogin: true, params: ById, body: PurgeInput, response: Purged, idempotencyKey: 'required' });

// --- The import (Chunk 7, R7, ADR 0019). A slice of at most IMPORT_SLICE rows at a time. ----------------

/** One slice of ITCBot's `members`. A dry run says what it would do and writes nothing; neither publishes an event. */
export const importMembers = defineEndpoint({ method: 'POST', path: '/members/admin/import/members', permission: 'members.admin', recentLogin: true, rateLimit: { max: 200 }, body: MemberImportInput, response: MembersImportReport, idempotencyKey: 'required' });

/** One slice of ITCBot's `organizations`. */
export const importOrganizations = defineEndpoint({ method: 'POST', path: '/members/admin/import/organizations', permission: 'members.admin', recentLogin: true, rateLimit: { max: 200 }, body: OrgImportInput, response: MembersImportReport, idempotencyKey: 'required' });

// --- Reports (plan 5F, R2). Each is made at the caller's tier, and its CSV on the server. ---------------------------------

const ByReport = z.object({ report: ViewReportParam });

/** The unlinked, roles and guest-anomaly reports, from one guild snapshot shared for a minute. A failed or empty snapshot fails the report. */
export const getReport = defineEndpoint({ method: 'GET', path: '/members/reports/:report', permission: 'members.view', params: ByReport, response: ReportView });

/** The threat report: everyone the threat system holds, with why. */
export const getThreatReport = defineEndpoint({ method: 'GET', path: '/members/reports/threats', permission: 'members.manage', response: ReportView });

/** A report as a CSV, made here so it holds what the caller's tier may see and nothing else. */
export const getReportCsv = defineEndpoint({ method: 'GET', path: '/members/reports/:report/csv', permission: 'members.view', params: ByReport, response: CsvFile });

export const getThreatReportCsv = defineEndpoint({ method: 'GET', path: '/members/reports/threats/csv', permission: 'members.manage', response: CsvFile });

/** The verification funnel and, for members.manage, the threat numbers, over time, from `members_snapshots`. */
export const getHistory = defineEndpoint({ method: 'GET', path: '/members/reports/history', permission: 'members.view', response: History });

/** A chart's worth of counts for each tracked role over a span (R1): a day, a week or a month. */
export const getRoleCounts = defineEndpoint({ method: 'GET', path: '/members/reports/role-counts', permission: 'members.view', query: RoleCountsQuery, response: RoleCountsView });

// --- The archive (plan 5F). Read-only, and for members.admin only. -----------------------------------------------------

/** ITCBot's action logs and commands, and the two ledgers, filtered and paged on the server. */
export const getArchive = defineEndpoint({ method: 'GET', path: '/members/archive', permission: 'members.admin', query: ArchiveQuery, response: ArchivePage });

// --- Importing ITCBot's history (plan 5F, R4, ADR 0019) ----------------------------------------------------------------

/** What the page needs to know of this server before it sends anything: its guild, and how long samples are kept. */
export const getHistoryImportContext = defineEndpoint({ method: 'GET', path: '/members/admin/history-context', permission: 'members.admin', response: HistoryImportContext });

/** One slice of `config/sync_history.json`. A dry run says what it would do and writes nothing. */
export const importSnapshots = defineEndpoint({ method: 'POST', path: '/members/admin/import/snapshots', permission: 'members.admin', recentLogin: true, rateLimit: { max: 600 }, body: SnapshotImportInput, response: HistoryImportReport, idempotencyKey: 'required' });

/** One slice of `reports/reports_stats.json`: this guild's readings only. */
export const importSamples = defineEndpoint({ method: 'POST', path: '/members/admin/import/samples', permission: 'members.admin', recentLogin: true, rateLimit: { max: 600 }, body: SampleImportInput, response: HistoryImportReport, idempotencyKey: 'required' });

/** One slice of ITCBot's `action_logs`, `commands` and ledger files, for the archive. */
export const importArchive = defineEndpoint({ method: 'POST', path: '/members/admin/import/archive', permission: 'members.admin', recentLogin: true, rateLimit: { max: 1200 }, body: ArchiveImportInput, response: HistoryImportReport, idempotencyKey: 'required' });
